// Validation utility functions for form inputs

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateEmail = (email) => {
  if (!email || !email.trim()) {
    return { isValid: false, error: 'Email is required' };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  if (email.length > 200) {
    return { isValid: false, error: 'Email cannot exceed 200 characters' };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @param {boolean} required - Whether phone is required
 * @returns {object} { isValid: boolean, error: string }
 */
export const validatePhone = (phone, required = false) => {
  if (!phone || !phone.trim()) {
    if (required) {
      return { isValid: false, error: 'Phone number is required' };
    }
    return { isValid: true, error: '' };
  }
  
  // Allow digits, spaces, hyphens, parentheses, and + for international format
  const phoneRegex = /^[\d\s\-\(\)\+]+$/;
  if (!phoneRegex.test(phone)) {
    return { isValid: false, error: 'Phone number can only contain digits, spaces, hyphens, parentheses, and +' };
  }
  
  if (phone.length > 50) {
    return { isValid: false, error: 'Phone number cannot exceed 50 characters' };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Validate name
 * @param {string} name - Name to validate
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateName = (name) => {
  if (!name || !name.trim()) {
    return { isValid: false, error: 'Name is required' };
  }
  
  if (name.trim().length < 1) {
    return { isValid: false, error: 'Name cannot be empty' };
  }
  
  if (name.length > 200) {
    return { isValid: false, error: 'Name cannot exceed 200 characters' };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Validate date of birth
 * @param {string|Date} dateOfBirth - Date of birth to validate
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateDateOfBirth = (dateOfBirth) => {
  if (!dateOfBirth) {
    return { isValid: true, error: '' }; // Optional field
  }
  
  const date = new Date(dateOfBirth);
  const minDate = new Date('1900-01-01');
  const today = new Date();
  
  if (isNaN(date.getTime())) {
    return { isValid: false, error: 'Please enter a valid date' };
  }
  
  if (date < minDate) {
    return { isValid: false, error: 'Date of birth must be after 1900' };
  }
  
  if (date > today) {
    return { isValid: false, error: 'Date of birth cannot be in the future' };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Validate age
 * @param {number} age - Age to validate
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateAge = (age) => {
  if (!age && age !== 0) {
    return { isValid: true, error: '' }; // Optional field
  }
  
  const ageNum = parseInt(age);
  
  if (isNaN(ageNum)) {
    return { isValid: false, error: 'Age must be a number' };
  }
  
  if (ageNum < 0 || ageNum > 150) {
    return { isValid: false, error: 'Age must be between 0 and 150' };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Validate year (graduation year, etc.)
 * @param {number} year - Year to validate
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateYear = (year) => {
  if (!year) {
    return { isValid: true, error: '' }; // Optional field
  }
  
  const yearNum = parseInt(year);
  const currentYear = new Date().getFullYear();
  
  if (isNaN(yearNum)) {
    return { isValid: false, error: 'Year must be a number' };
  }
  
  if (yearNum < 1900 || yearNum > currentYear + 20) {
    return { isValid: false, error: `Year must be between 1900 and ${currentYear + 20}` };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Validate text field with max length
 * @param {string} text - Text to validate
 * @param {number} maxLength - Maximum length allowed
 * @param {string} fieldName - Name of the field for error message
 * @param {boolean} required - Whether field is required
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateTextField = (text, maxLength, fieldName, required = false) => {
  if (!text || !text.trim()) {
    if (required) {
      return { isValid: false, error: `${fieldName} is required` };
    }
    return { isValid: true, error: '' };
  }
  
  if (text.length > maxLength) {
    return { isValid: false, error: `${fieldName} cannot exceed ${maxLength} characters` };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Validate URL format
 * @param {string} url - URL to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateURL = (url, fieldName = 'URL') => {
  if (!url || !url.trim()) {
    return { isValid: true, error: '' }; // Optional field
  }
  
  try {
    new URL(url);
    if (url.length > 300) {
      return { isValid: false, error: `${fieldName} cannot exceed 300 characters` };
    }
    return { isValid: true, error: '' };
  } catch (e) {
    return { isValid: false, error: `Please enter a valid ${fieldName}` };
  }
};

/**
 * Validate rate (hourly rate, etc.)
 * @param {number} rate - Rate to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateRate = (rate, fieldName = 'Rate') => {
  if (!rate && rate !== 0) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  const rateNum = parseFloat(rate);
  
  if (isNaN(rateNum)) {
    return { isValid: false, error: `${fieldName} must be a number` };
  }
  
  if (rateNum < 0) {
    return { isValid: false, error: `${fieldName} cannot be negative` };
  }
  
  if (rateNum > 10000) {
    return { isValid: false, error: `${fieldName} seems unusually high (max: 10000)` };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Validate frequency per month
 * @param {number} frequency - Frequency to validate
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateFrequency = (frequency) => {
  if (!frequency && frequency !== 0) {
    return { isValid: true, error: '' }; // Optional field
  }
  
  const freqNum = parseInt(frequency);
  
  if (isNaN(freqNum)) {
    return { isValid: false, error: 'Frequency must be a number' };
  }
  
  if (freqNum < 0 || freqNum > 100) {
    return { isValid: false, error: 'Frequency must be between 0 and 100' };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Validate session duration (in minutes)
 * @param {number} duration - Duration to validate
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateDuration = (duration) => {
  if (!duration && duration !== 0) {
    return { isValid: true, error: '' }; // Optional field
  }
  
  const durationNum = parseInt(duration);
  
  if (isNaN(durationNum)) {
    return { isValid: false, error: 'Duration must be a number' };
  }
  
  if (durationNum < 15 || durationNum > 480) {
    return { isValid: false, error: 'Duration must be between 15 and 480 minutes' };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Validate entire tutor form
 * @param {object} formData - Form data to validate
 * @returns {object} { isValid: boolean, errors: object }
 */
export const validateTutorForm = (formData) => {
  const errors = {};
  
  // Required fields
  const nameValidation = validateName(formData.name);
  if (!nameValidation.isValid) errors.name = nameValidation.error;
  
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) errors.email = emailValidation.error;
  
  // Optional fields
  if (formData.dateOfBirth) {
    const dobValidation = validateDateOfBirth(formData.dateOfBirth);
    if (!dobValidation.isValid) errors.dateOfBirth = dobValidation.error;
  }
  
  if (formData.phone) {
    const phoneValidation = validatePhone(formData.phone);
    if (!phoneValidation.isValid) errors.phone = phoneValidation.error;
  }
  
  if (formData.address) {
    const addressValidation = validateTextField(formData.address, 500, 'Address');
    if (!addressValidation.isValid) errors.address = addressValidation.error;
  }
  
  if (formData.linkedin) {
    const linkedinValidation = validateURL(formData.linkedin, 'LinkedIn URL');
    if (!linkedinValidation.isValid) errors.linkedin = linkedinValidation.error;
  }
  
  if (formData.bio) {
    const bioValidation = validateTextField(formData.bio, 2000, 'Bio');
    if (!bioValidation.isValid) errors.bio = bioValidation.error;
  }
  
  if (formData.school) {
    const schoolValidation = validateTextField(formData.school, 200, 'School');
    if (!schoolValidation.isValid) errors.school = schoolValidation.error;
  }
  
  if (formData.major) {
    const majorValidation = validateTextField(formData.major, 200, 'Major');
    if (!majorValidation.isValid) errors.major = majorValidation.error;
  }
  
  if (formData.year) {
    const yearValidation = validateYear(formData.year);
    if (!yearValidation.isValid) errors.year = yearValidation.error;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate entire tutee form
 * @param {object} formData - Form data to validate
 * @returns {object} { isValid: boolean, errors: object }
 */
export const validateTuteeForm = (formData) => {
  const errors = {};
  
  // Required fields
  const nameValidation = validateName(formData.name);
  if (!nameValidation.isValid) errors.name = nameValidation.error;
  
  // Optional fields
  if (formData.email) {
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) errors.email = emailValidation.error;
  }
  
  if (formData.phone) {
    const phoneValidation = validatePhone(formData.phone);
    if (!phoneValidation.isValid) errors.phone = phoneValidation.error;
  }
  
  if (formData.age) {
    const ageValidation = validateAge(formData.age);
    if (!ageValidation.isValid) errors.age = ageValidation.error;
  }
  
  if (formData.parent?.phone) {
    const parentPhoneValidation = validatePhone(formData.parent.phone);
    if (!parentPhoneValidation.isValid) errors.parentPhone = parentPhoneValidation.error;
  }
  
  if (formData.parent?.email) {
    const parentEmailValidation = validateEmail(formData.parent.email);
    if (!parentEmailValidation.isValid) errors.parentEmail = parentEmailValidation.error;
  }
  
  if (formData.school) {
    const schoolValidation = validateTextField(formData.school, 200, 'School');
    if (!schoolValidation.isValid) errors.school = schoolValidation.error;
  }
  
  if (formData.notes) {
    const notesValidation = validateTextField(formData.notes, 2000, 'Notes');
    if (!notesValidation.isValid) errors.notes = notesValidation.error;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate subject form
 * @param {object} subjectData - Subject data to validate
 * @returns {object} { isValid: boolean, errors: object }
 */
export const validateSubjectForm = (subjectData) => {
  const errors = {};
  
  // Required fields
  const nameValidation = validateTextField(subjectData.name, 200, 'Subject name', true);
  if (!nameValidation.isValid) errors.name = nameValidation.error;
  
  const rateValidation = validateRate(subjectData.rate, 'Hourly rate');
  if (!rateValidation.isValid) errors.rate = rateValidation.error;
  
  // Optional fields
  if (subjectData.frequencyPerMonth) {
    const freqValidation = validateFrequency(subjectData.frequencyPerMonth);
    if (!freqValidation.isValid) errors.frequencyPerMonth = freqValidation.error;
  }
  
  if (subjectData.sessionDuration) {
    const durationValidation = validateDuration(subjectData.sessionDuration);
    if (!durationValidation.isValid) errors.sessionDuration = durationValidation.error;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
